<template>
    <div class="dashboard-banners">
        <!-- TPO Onboarding Alert - Not Yet Started -->
        <div v-if="needsOnboarding" class="tpo-onboarding-alert fadeIn">
            <div class="alert-inner">
                <div class="alert-content">
                    <div class="alert-icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-body">
                        <h2><strong>Action Required:</strong> Complete Your Payment Plan Setup</h2>
                        <p>You have a payment plan that needs to be set up with our payment partner, The Payment Option. Please complete the onboarding process to activate your plan.</p>
                        <div class="alert-actions">
                            <a :href="tpoOnboardingUrl" target="_blank" class="btn-primary">
                                <i class="fas fa-arrow-right mr-2"></i>Complete Setup Now
                            </a>
                            <a href="mailto:support@my247ed.com?subject=No Longer Interested in Payment Plan&body=I am no longer interested in completing my payment plan setup. Please contact me to discuss my options." class="btn-secondary">
                                I'm no longer interested...
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- TPO Agreement Pending -->
        <div v-else-if="needsDocuSign" class="tpo-onboarding-alert fadeIn">
            <div class="alert-inner">
                <div class="alert-content">
                    <div class="alert-icon">
                        <i class="fas fa-file-signature"></i>
                    </div>
                    <div class="alert-body">
                        <h2><strong>Action Required:</strong> Complete Your Payment Plan Agreement</h2>
                        <p>You have a pending agreement with The Payment Option that needs to be completed. Please click the button below to review and sign your agreement.</p>
                        <div class="alert-actions">
                            <a :href="tpoPendingAgreementUrl" target="_blank" class="btn-primary">
                                <i class="fas fa-file-signature mr-2"></i>Complete Agreement
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cart Enrollment Alert -->
        <div v-if="hasCartEnrollment" class="tpo-onboarding-alert cart-alert fadeIn">
            <div class="alert-inner">
                <div class="alert-content">
                    <div class="alert-icon">
                        <i class="fad fa-shopping-cart"></i>
                    </div>
                    <div class="alert-body">
                        <h2><strong>Action Required:</strong> Complete Your Cart Enrollment</h2>
                        <p><span v-if="cartQuote.reseller_name"><strong>{{ cartQuote.reseller_name }}</strong> has prepared </span><span v-else>You have </span><strong>{{ courseCount }} {{ courseCount === 1 ? 'course' : 'courses' }}</strong> ({{ cartQuote.hours }} clock hours) for you and they are ready for checkout for <strong>{{ cartQuote.total | currency }}</strong>.</p>
                        <div class="alert-actions">
                            <router-link to="/cart-checkout" class="btn-primary">
                                <i class="fas fa-credit-card mr-2"></i>Complete Checkout
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Unpaid Sales Tax Alert -->
        <div v-if="hasUnpaidSalesTax" class="tpo-onboarding-alert tax-alert fadeIn">
            <div class="alert-inner">
                <div class="alert-content">
                    <div class="alert-icon">
                        <i class="fas fa-exclamation-triangle"></i>
                    </div>
                    <div class="alert-body">
                        <h2><strong>Action Required:</strong> Sales Tax Payment Due</h2>
                        <p>The Washington state legislature enacted a retroactive sales tax on existing contracts with any existing balance as of October 1, 2025, regardless whether your payment plan has started or not started. Due to this sales tax change, sales tax must now be collected.</p>
                        <p>Please refer to the email notification of your Washington State Sales Tax Payment sent September, 2025 for more details.</p>
                        <p>Sales tax is a separate transaction from any regular or additional payment, and can be made with a credit card. Payment is made at our lending partner, The Payment Option. Click the link below to login to your account and pay your sales tax.</p>
                        <div class="alert-actions">
                            <a href="https://app.thepaymentoption.com" target="_blank" class="btn-primary">
                                <i class="fas fa-credit-card mr-2"></i>Login to Pay Sales Tax
                            </a>
                            <a href="mailto:support@my247ed.com?subject=Sales Tax Question" class="btn-secondary">
                                <i class="fas fa-envelope mr-2"></i>Contact Support
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Missing Payment Method Alert -->
        <div v-if="needsPaymentMethod" class="tpo-onboarding-alert tax-alert fadeIn">
            <div class="alert-inner">
                <div class="alert-content">
                    <div class="alert-icon">
                        <i class="fas fa-exclamation-triangle"></i>
                    </div>
                    <div class="alert-body">
                        <h2><strong>Action Required:</strong> Payment Method Needed</h2>
                        <p>One or more of your active payment plans does not have a payment method on file. A valid payment method is required to keep your payment plan current.</p>
                        <p>Your payment plan is managed by our lending partner, The Payment Option. Click the link below to log in to your account and add a payment method.</p>
                        <div class="alert-actions">
                            <a href="https://app.thepaymentoption.com" target="_blank" class="btn-primary">
                                <i class="fas fa-credit-card mr-2"></i>Login to Add Payment Method
                            </a>
                            <a href="mailto:support@my247ed.com?subject=Payment Method Question" class="btn-secondary">
                                <i class="fas fa-envelope mr-2"></i>Contact Support
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Expired Courses Alert -->
        <div v-if="hasExpiredCourses" class="tpo-onboarding-alert expired-alert fadeIn">
            <div class="alert-inner">
                <div class="alert-content">
                    <div class="alert-icon">
                        <i class="fas fa-clock"></i>
                    </div>
                    <div class="alert-body">
                        <h2><strong>Attention:</strong> You Have Expired Courses</h2>
                        <p>You have <strong>{{ expiredCourses.count }} expired {{ expiredCourses.count === 1 ? 'course' : 'courses' }}</strong> that can be reactivated. Please click on Reactivate My Courses to explore your options.</p>
                        <div class="alert-actions">
                            <router-link to="/reactivation" class="btn-primary">
                                <i class="fas fa-redo mr-2"></i>Reactivate My Courses
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Profile Verification Reminder (gentle) -->
        <div v-if="profilePrompt.pending" class="tpo-onboarding-alert profile-reminder-alert fadeIn">
            <div class="alert-inner">
                <div class="alert-content">
                    <div class="alert-icon">
                        <i class="fad fa-user-check"></i>
                    </div>
                    <div class="alert-body">
                        <h2><strong>Friendly Reminder:</strong> Your Info Needs a Quick Review</h2>
                        <p>You still have a pending information update. It only takes a minute to verify your contact details and answer a few quick questions — keeping this current helps us keep your records and certifications accurate.</p>
                        <div class="alert-actions">
                            <a href @click.prevent="openProfileVerification" class="btn-primary">
                                <i class="fas fa-user-check mr-2"></i>Review My Info
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapMutations} from "vuex";

    /**
     * The dashboard's money-critical and account banners. Markup is the
     * original Dashboard.vue stack, unchanged; the predicates now come from
     * the shared paymentPlans store so the Account page and the dashboard
     * panels read the same data.
     */
    export default {
        name: "DashboardBanners",
        computed: {
            ...mapGetters([
                'profilePrompt',
                'needsOnboarding', 'needsDocuSign', 'tpoOnboardingUrl', 'tpoPendingAgreementUrl',
                'hasUnpaidSalesTax', 'needsPaymentMethod',
                'hasCartEnrollment', 'cartQuotes',
                'hasExpiredCourses', 'expiredCourses',
            ]),
            cartQuote: function() {
                return this.cartQuotes.length > 0 ? this.cartQuotes[0] : {};
            },
            courseCount: function() {
                if (!this.cartQuote.course_choices) return 0;
                const courses = typeof this.cartQuote.course_choices === 'string'
                    ? JSON.parse(this.cartQuote.course_choices)
                    : this.cartQuote.course_choices;
                return courses ? courses.length : 0;
            },
            // True when at least one banner is on screen (the page uses it
            // to decide what the attention list may leave out)
            hasAny: function() {
                return this.needsOnboarding || this.needsDocuSign || this.hasCartEnrollment
                    || this.hasUnpaidSalesTax || this.needsPaymentMethod || this.hasExpiredCourses
                    || this.profilePrompt.pending;
            }
        },
        methods: {
            ...mapMutations(['setProfilePromptRequested']),
            openProfileVerification() {
                this.setProfilePromptRequested(true);
            },
        }
    };
</script>

<style lang="scss" scoped>
    .tpo-onboarding-alert {
        background-color: white;
        border-radius: 0.5rem;
        padding: 0.5rem;
        margin-bottom: 4rem;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);

        .alert-inner {
            border: 2px solid #ffc107;
            border-radius: 0.375rem;
            padding: 1.5rem;
        }

        &.cart-alert {
            margin-bottom: 5rem;

            .alert-inner {
                border-color: #ffc107;
            }
        }

        &.profile-reminder-alert {
            box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.2);

            .alert-inner {
                border-color: #336699;
                border-width: 1px;
            }

            .alert-icon {
                color: #336699 !important;
            }

            .alert-body h2 strong {
                color: #336699 !important;
            }

            .alert-actions .btn-primary {
                background: #336699 !important;
                color: #fff !important;

                &:hover {
                    background: #28517a !important;
                }
            }
        }

        &.tax-alert {

            .alert-inner {
                border-color: #dc3545;
            }

            .alert-icon {
                color: #dc3545 !important;
            }

            .alert-body h2 strong {
                color: #dc3545 !important;
            }

            .alert-actions .btn-primary {
                background: #dc3545 !important;
                color: #fff !important;

                &:hover {
                    background: #c82333 !important;
                }
            }
        }

        .alert-content {
            display: flex;
            align-items: flex-start;
            gap: 1.25rem;

            .alert-icon {
                font-size: 2rem;
                color: #ffc107;
                flex-shrink: 0;
                margin-top: -0.5rem;
            }

            .alert-body {
                flex: 1;

                h2 {
                    font-size: 1.15rem;
                    color: #333;
                    margin-bottom: 0.5rem;
                    font-weight: 600;

                    strong {
                        color: #d49a00;
                    }
                }

                p {
                    color: #555;
                    font-size: 0.95rem;
                    line-height: 1.5;
                    margin-bottom: 1.25rem;
                }

                .alert-actions {
                    display: flex;
                    gap: 0.75rem;
                    flex-wrap: wrap;

                    a {
                        text-decoration: none;
                        padding: 0.65rem 1.25rem;
                        border-radius: 0.375rem;
                        font-weight: 500;
                        transition: all 0.15s ease;
                        display: inline-flex;
                        align-items: center;
                        font-size: 0.9rem;

                        &.btn-primary {
                            background: #ffc107;
                            color: #1e3a5f;

                            &:hover {
                                background: #e6ad00;
                                transform: translateY(-1px);
                            }
                        }

                        &.btn-secondary {
                            background: transparent;
                            color: #666;
                            border: 1px solid #ccc;
                            font-size: 0.85rem;

                            &:hover {
                                background: #f5f5f5;
                                color: #333;
                                border-color: #999;
                            }
                        }
                    }
                }
            }
        }

        @media (max-width: 768px) {
            padding: 0.375rem;

            .alert-inner {
                padding: 1.25rem;
            }

            .alert-content {
                gap: 1rem;

                .alert-icon {
                    font-size: 1.5rem;
                }

                .alert-body {
                    h2 {
                        font-size: 1.05rem;
                    }

                    p {
                        font-size: 0.9rem;
                    }

                    .alert-actions {
                        flex-direction: column;
                        gap: 0.5rem;

                        a {
                            width: 100%;
                            justify-content: center;
                        }
                    }
                }
            }
        }
    }
</style>
